import React, { Fragment, useEffect, useRef } from 'react';
import { hdrm } from '@yola/ws-sdk';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';

const { Autocomplete, BetaLabel, Paragraph, Loader, ActionButton } = designSystem;
const { DISABLE_BLUR_ATTR } = hdrm.constants.attributes;
const disableBlurAttribute = { [DISABLE_BLUR_ATTR]: true };

const SELECT_MENU_WIDTH = 'min(330px, 100%)';
const SELECT_MENU_MAX_HEIGHT = 306;

const AiTextAssistant = ({
  captions,
  autocompleteOptions,
  isMobile,
  isLoading,
  isRetryAvailable,
  userInputId,
  userInput,
  actionButtonProps,
  filterAutocompleteOptions,
  onChange,
  onInputChange,
  onKeyDown,
  onCancel,
  position,
  selectedAiRewriteOption = null,
}) => {
  const autocompleteRef = useRef(null);
  const wrapperRef = useRef(null);
  const { loading, placeholder, noResultsText, inputPrefix } = captions;

  useEffect(() => {
    if (selectedAiRewriteOption && !autocompleteOptions.length) {
      autocompleteRef.current.selectOption(selectedAiRewriteOption);
    }

    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [selectedAiRewriteOption]);

  useEffect(() => {
    const clickHandler = (e) => {
      if (!wrapperRef.current.contains(e.target)) {
        onCancel({ isOverlayClicked: true });
      }
    };

    document.addEventListener('click', clickHandler);

    return () => document.removeEventListener('click', clickHandler);
  }, [onCancel]);

  return (
    <div
      className="ws-ai-text-assistant"
      style={position}
      {...disableBlurAttribute}
      ref={wrapperRef}
      onClick={(e) => e.nativeEvent.stopImmediatePropagation()}
    >
      {isLoading ? (
        <div className="ws-ai-text-assistant__loader">
          <BetaLabel />
          <Paragraph>{loading}</Paragraph>
          <Loader />
        </div>
      ) : (
        <Fragment>
          <div className="ws-ai-text-assistant__select">
            <Autocomplete
              id={userInputId}
              ref={autocompleteRef}
              defaultInputValue={userInput}
              options={autocompleteOptions}
              placeholder={inputPrefix ? '' : placeholder}
              size="medium"
              filterOptionsFunction={filterAutocompleteOptions}
              {...(!inputPrefix && { iconComponent: <BetaLabel /> })}
              {...(inputPrefix && {
                prefixTextComponent: (
                  <Paragraph appearance="medium-emphasis">{inputPrefix}</Paragraph>
                ),
              })}
              defaultMenuIsOpen={!isRetryAvailable}
              openMenuOnFocus
              autoFocus
              transparent
              clearable={false}
              menuWidth={SELECT_MENU_WIDTH}
              {...(!isMobile && { maxMenuHeight: SELECT_MENU_MAX_HEIGHT })}
              onChange={onChange}
              onInputChange={onInputChange}
              onKeyDown={onKeyDown}
              noResultsText={noResultsText}
            />
          </div>
          {userInput.trim() && (
            <ActionButton format="solid-white" size="small" {...actionButtonProps} />
          )}
        </Fragment>
      )}
      <ActionButton format="solid-white" size="small" iconGlyph="close" onClick={onCancel} />
    </div>
  );
};

AiTextAssistant.propTypes = {
  captions: PropTypes.shape({
    placeholder: PropTypes.string.isRequired,
    loading: PropTypes.string.isRequired,
    cancel: PropTypes.string.isRequired,
    noResultsText: PropTypes.string,
    inputPrefix: PropTypes.string,
  }).isRequired,
  autocompleteOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.shape(),
    })
  ).isRequired,
  isMobile: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isRetryAvailable: PropTypes.bool.isRequired,
  userInputId: PropTypes.string.isRequired,
  userInput: PropTypes.string.isRequired,
  actionButtonProps: PropTypes.shape({
    iconGlyph: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  }).isRequired,
  filterAutocompleteOptions: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  position: PropTypes.shape({
    top: PropTypes.number,
    left: PropTypes.number,
    width: PropTypes.number,
  }).isRequired,
  selectedAiRewriteOption: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
};

export default AiTextAssistant;
